import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "./cn";
import { FieldLabel } from "./FieldLabel";
import { codeToFlagEmoji } from "./countryFlag";
import { Countries } from "../../public/Home/CountryFlag";
import { ChevronDown, CheckIcon } from "../../../assets/ui/icons";
import { ReactComponent as Magnifier } from "../../../assets/icons/Magnifier.svg";
import { ReactComponent as GPS } from "../../../assets/icons/GPS.svg";

/* The country picker, shared by every u1 screen that needs one. */

/* GCC markets are shown first, matching the hero dropdown's ordering. */
export const GCC_CODES = ["AE", "SA", "QA", "KW", "BH", "OM"];

export const CountrySelect = ({
  value,
  onChange = () => {},
  onLocate,
  variant = "field",
  /* "bar" only: "md" is the search page's band, "sm" the dashboard top bar,
     where the whole field is 46px tall and the trigger has to fit inside it. */
  size = "md",
  /* The country's name costs ~150px. The page band always has room; the top
     bar does not until the viewport is wide, so it passes a class that hides
     the name and leaves flag + chevron. */
  nameClassName = "hidden sm:inline",
  label,
  id = "country-select",
  required = false,
  placeholder = "Select Country",
  error,
  /* The edit-profile forms fix the country once a searched business or an
     existing record has decided it, so the trigger has to be able to say so.
     Default false, so every existing caller is unchanged. */
  disabled = false,
  /* Forwarded to FieldLabel, as SelectField does — the intake screens keep the
     tracked caption, the edit-profile forms use the sentence-case label. */
  labelVariant,
  /* What the trigger announces itself as. */
  ariaLabel,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [rect, setRect] = useState(null);

  const selected = Countries.find((c) => c.code === value) || null;

  // A menu left open behind a click elsewhere would sit over the page.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      /* The menu is portalled out of `ref`, so it needs checking separately —
         otherwise a mousedown inside it counts as a click elsewhere. */
      if (menuRef.current?.contains(e.target)) return;
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // The query is stale the moment the menu closes.
  useEffect(() => {
    if (!open) setTerm("");
  }, [open]);

  const q = term.trim().toLowerCase();
  const matches = (c) =>
    !q || c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q);
  const gcc = GCC_CODES.map((code) => Countries.find((c) => c.code === code))
    .filter(Boolean)
    .filter(matches);
  const others = Countries.filter((c) => !GCC_CODES.includes(c.code)).filter(matches);

  const choose = (country) => {
    onChange(country);
    setOpen(false);
  };

  const toggle = () => {
    if (disabled) return;
    setOpen((o) => !o);
  };

  /* Measured before paint so the menu never appears at the wrong place first.
     The width follows the trigger for a form field and keeps the design's
     300px for the compact bar trigger, which is far narrower than its menu. */
  useLayoutEffect(() => {
    if (!open) return undefined;

    const measure = () => {
      const box = triggerRef.current?.getBoundingClientRect();
      if (!box) return;

      /* DROP UP WHEN THERE IS NO ROOM BELOW. */
      const MENU_MAX = 440;
      const GAP = 6;
      const measured = menuRef.current?.offsetHeight;
      const needed = Math.min(measured || MENU_MAX, MENU_MAX);

      const below = window.innerHeight - box.bottom - GAP;
      const above = box.top - GAP;
      /* MARGIN, so a few pixels of difference cannot flip the menu. */
      const FLIP_MARGIN = 24;
      const dropUp = below < needed && above > below + FLIP_MARGIN;

      setRect({
        top: dropUp ? Math.max(GAP, box.top - GAP - needed) : box.bottom + GAP,
        left: box.left,
        width: box.width,
        /* Height is capped to whichever side it opened into, so the menu is
           never taller than the space it has — it scrolls instead. */
        maxHeight: Math.max(160, Math.min(needed, dropUp ? above : below)),
        dropUp,
      });
    };
    measure();
    /* A second pass once the menu exists: the first runs before it has been
       laid out, so `measured` is unavailable and the cap is assumed. */
    const raf = requestAnimationFrame(measure);

    /* A fixed menu does not travel with a scrolling container, so rather than let it drift away from its trigger it closes. */
    const onScroll = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const close = () => setOpen(false);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  const locate = (e) => {
    e?.preventDefault?.();
    if (onLocate) onLocate(choose);
    setOpen(false);
  };

  const row = (option) => {
    const isCurrent = value === option.code;
    return (
      <button
        key={option.code}
        type="button"
        className="u1-country-row"
        aria-current={isCurrent ? "true" : undefined}
        onClick={() => choose(option)}
      >
        <span className="u1-country-flag-emoji">{codeToFlagEmoji(option.code)}</span>
        <span className="u1-country-row-name">{option.name}</span>
        <span className="u1-country-code">{option.code}</span>
        {isCurrent && <CheckIcon />}
      </button>
    );
  };

  const errorId = `${id}-error`;
  const triggerName = `${ariaLabel || label || "Country"}: ${
    selected?.name || placeholder
  }`;

  const trigger =
    variant === "bar" ? (
      <button
        type="button"
        ref={triggerRef}
        onClick={toggle}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={triggerName}
        className={cn(
          "flex items-center gap-2 rounded-lg font-medium text-[var(--ink)] font-sans",
          "disabled:cursor-not-allowed disabled:opacity-60",
          "hover:bg-[var(--slate-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--orange)]",
          size === "sm" ? "h-[34px] px-2 text-[13.5px]" : "h-11 px-3 text-[14px]"
        )}
      >
        <span className="u1-country-flag-emoji" aria-hidden="true">
          {codeToFlagEmoji(value)}
        </span>
        <span className={cn("whitespace-nowrap", nameClassName)}>
          {selected?.name || placeholder}
        </span>
        <ChevronDown open={open} />
      </button>
    ) : (
      <button
        type="button"
        id={id}
        ref={triggerRef}
        onClick={toggle}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={triggerName}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={cn(
          "flex w-full items-center gap-2 rounded-lg border px-3.5 py-[11px] text-left text-[14px] text-[var(--ink)] font-sans",
          "transition-[border-color,box-shadow] duration-150",
          disabled
            ? "cursor-not-allowed bg-[var(--field-inset)] opacity-80"
            : "bg-white",
          "focus:outline-none focus:border-[var(--orange)] focus:shadow-[0_0_0_4px_var(--focus-ring)]",
          error
            ? "border-[var(--coral)]"
            : "border-[var(--field-border)] hover:border-[var(--field-border-hover)]"
        )}
      >
        <span className="u1-country-flag-emoji" aria-hidden="true">
          {codeToFlagEmoji(value)}
        </span>
        <span className="min-w-0 flex-1 truncate">{selected?.name || placeholder}</span>
        <span className="ml-auto shrink-0 pl-2 text-[var(--muted-2)]">
          <ChevronDown open={open} />
        </span>
      </button>
    );

  return (
    <div className={className}>
      {label && variant !== "bar" && (
        <FieldLabel htmlFor={id} required={required} variant={labelVariant} className="mb-2">
          {label}
        </FieldLabel>
      )}

      {/* `static` on phones hands the menu's containing block to the row above,
          so it spans the full field width rather than the trigger's; sm:relative
          restores anchoring on larger screens. */}
      <div className="static shrink-0 sm:relative" ref={ref}>
        {trigger}

        {open &&
          createPortal(
            /* Wrapped in u1-root: every .u1-country-* rule and every design
               token is scoped to that class, and the portal lands outside it. */
            <div className="u1-root">
              <div
                ref={menuRef}
                className="u1-country-menu"
                /* Position is measured, so it cannot be a Tailwind class.
                   `fixed` is what escapes the dialog's scrolling body. */
                data-drop={rect?.dropUp ? "up" : "down"}
                style={
                  rect
                    ? {
                        position: "fixed",
                        top: rect.top,
                        left: rect.left,
                        /* A form field's menu matches its width; the compact
                           bar trigger keeps the design's own 300px. */
                        width: variant === "bar" ? undefined : Math.max(rect.width, 260),
                        maxHeight: rect.maxHeight,
                      }
                    : undefined
                }
              >
            <div className="u1-country-search">
              <Magnifier />
              <input
                type="text"
                placeholder="Search country"
                aria-label="Search country"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
              />
            </div>

            {onLocate && (
              <button type="button" className="u1-country-gps" onClick={locate}>
                <GPS /> Locate Using GPS
              </button>
            )}

            <div className="u1-country-list">
              {gcc.length > 0 && (
                <>
                  <div className="u1-country-group">GCC · Primary market</div>
                  {gcc.map(row)}
                </>
              )}
              {others.length > 0 && (
                <>
                  <div className="u1-country-group">Other countries</div>
                  {others.map(row)}
                </>
              )}
              {gcc.length === 0 && others.length === 0 && (
                <div className="u1-country-empty">No country matches “{term}”</div>
              )}
                </div>
              </div>
            </div>,
            document.body
          )}
      </div>

      {error && (
        <p id={errorId} role="alert" className="mt-1.5 text-[12px] text-[var(--coral-deep)] font-sans">
          {error}
        </p>
      )}
    </div>
  );
};

export default CountrySelect;
